import { Box, HStack, VStack, Icon, Heading, SimpleGrid } from '@chakra-ui/react';
import { FaFileContract } from 'react-icons/fa';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Skeleton,
} from '@chakra-ui/react';
import { IContrat } from '@/service/club/types';
import { ContractCard } from './ContractCard';
import { ListPagination } from './ListPagination';
import { Label } from '@/components/atoms/Label';

interface ContratosModalProps {
  isOpen: boolean;
  onClose: () => void;
  contratos: IContrat[];
  colors: { primaryColor: string };
  selectedContrato?: IContrat | null;
  onSelectContrato?: (contrato: IContrat) => void;
  onBoletoClick?: (url: string) => void;
  count: number;
  page: number;
  perPage: number;
  hasPrevious: boolean;
  hasNext: boolean;
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
  onPrevious: () => void;
  onNext: () => void;
}

export function ContratosModal({
  isOpen,
  onClose,
  contratos,
  colors,
  selectedContrato,
  onSelectContrato,
  onBoletoClick,
  count,
  page,
  perPage,
  hasPrevious,
  hasNext,
  isLoading = false,
  isError = false,
  errorMessage,
  onPrevious,
  onNext,
}: ContratosModalProps) {
  const getContractId = (c: IContrat): string | null => {
    const n = c.numero_contrato != null ? String(c.numero_contrato).trim() : null;
    if (n) return n;
    const cod = c.codigo_contrato != null ? String(c.codigo_contrato).trim() : null;
    if (cod) return cod;
    const id = (c as any).id_cliente_servico != null ? String((c as any).id_cliente_servico).trim() : null;
    return id;
  };

  const isSameContract = (a: IContrat, b: IContrat): boolean => {
    const idA = getContractId(a);
    const idB = getContractId(b);
    return idA != null && idB != null && idA === idB;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent
        maxW={{ base: '95vw', sm: '90vw', md: '4xl', lg: '5xl' }}
        borderRadius="xl"
        overflow="hidden"
        boxShadow="xl"
      >
        <ModalHeader
          py={6}
          px={6}
          borderBottom="1px solid"
          borderColor="gray.100"
          backgroundColor="gray.50"
        >
          <HStack spacing={3}>
            <Box
              p={2}
              borderRadius="lg"
              backgroundColor={`${colors.primaryColor}15`}
              color={colors.primaryColor}
            >
              <Icon as={FaFileContract} boxSize={6} />
            </Box>
            <VStack align="flex-start" spacing={0}>
              <Heading size="md" color="gray.800" fontWeight="600">
                Lista de Contratos
              </Heading>
              <Label fontSize="sm" color="gray.500" fontWeight="400">
                {isError
                  ? errorMessage || 'Não foi possível consultar os contratos.'
                  : isLoading && count === 0
                    ? 'Carregando contratos...'
                    : `${count} ${count === 1 ? 'contrato no total' : 'contratos no total'}`}
              </Label>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton top={4} right={4} size="lg" borderRadius="full" />
        <ModalBody py={6} px={6} maxHeight={{ base: '70vh', md: '65vh' }} overflowY="auto">
          {isError ? (
            <VStack py={12} spacing={4}>
              <Icon as={FaFileContract} boxSize={12} color="gray.300" />
              <Label color="gray.500" textAlign="center">
                {errorMessage || 'Não foi possível consultar os contratos.'}
              </Label>
            </VStack>
          ) : isLoading && !contratos?.length ? (
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={4} width="100%">
              {Array.from({ length: 3 }).map((_, index) => (
                <Skeleton key={index} height="140px" borderRadius="lg" />
              ))}
            </SimpleGrid>
          ) : !contratos?.length ? (
            <VStack py={12} spacing={4}>
              <Icon as={FaFileContract} boxSize={12} color="gray.300" />
              <Label color="gray.500">Nenhum contrato encontrado</Label>
            </VStack>
          ) : (
            <SimpleGrid
              columns={{ base: 1, sm: 2, lg: 3 }}
              spacing={4}
              width="100%"
              opacity={isLoading ? 0.6 : 1}
            >
              {contratos.map((contrato, index) => {
                const matches = selectedContrato != null && isSameContract(contrato, selectedContrato);
                const selectedId = selectedContrato ? getContractId(selectedContrato) : null;
                const firstMatchIndex = selectedId
                  ? contratos.findIndex((c) => getContractId(c) === selectedId)
                  : -1;
                const isSelected = matches && index === firstMatchIndex;
                return (
                  <ContractCard
                    key={index}
                    contrato={contrato}
                    colors={colors}
                    onClick={() => {
                      onSelectContrato?.(contrato);
                      onClose();
                    }}
                    isSelected={isSelected}
                    onBoletoClick={onBoletoClick}
                  />
                );
              })}
            </SimpleGrid>
          )}
        </ModalBody>
        {!isError && count > 0 && (
          <ModalFooter borderTop="1px solid" borderColor="gray.100" width="100%">
            <ListPagination
              page={page}
              count={count}
              perPage={perPage}
              hasPrevious={hasPrevious}
              hasNext={hasNext}
              isLoading={isLoading}
              onPrevious={onPrevious}
              onNext={onNext}
              singular="contrato"
              plural="contratos"
              color={colors.primaryColor}
            />
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
}
